// @ts-check
import { defineStore } from "pinia";
import { setCookie, getCookieByName } from "../js/helpers/cookies.utils";
import { useFacebookPixel } from "../js/services/metaPixel.service";
import { useGoogleAnalytics } from "../js/services/googleAnalytics.service";
import type { ClientSettings } from "@/plugins/useClientSettings";

const consentedOnCookieKey = "usr-consent-date" as const;

// cookie consent + the individual preferences expire after 90 days (deleted from browser) - user will be asked to set their preferences again
// we do this as a failsafe in case new cookies are added to the app which then need to be accepted/rejected by the user
// This variable is exported so that we can use it to re-show the cookie consent popup after 90 days
const cookieConsentExpiryDays = 90; // days

interface CookieCategory {
   isNecessary: boolean;
   category: string;
   description: string;
   cookies: (Cookie | ServiceCookie)[];
}

// different types of cookie settings that are used in the app
type CookieId = "authentication" | "cookiePreferences" | "intercom" | "metaPixel" | "googleAnalytics";

interface Cookie {
   id: CookieId;
   name: string;
   description: string;
}
interface ServiceCookie extends Cookie {
   cookieStorageKey: string; // the key used to store the user cookie preference in the browser
   isActivated: boolean; // activation could be either by the user or compulsory if necessary cookie
   code: string; // the code used to initialise the service (ex. Google Analytics "G-XXXXX")
}

const useCookieSettingsStore = defineStore("cookieSettings", () => {
   const cookieCategories = ref<CookieCategory[]>([]);

   function getNecessaryCategory($t: (key: string) => string, clientSettings: ClientSettings): CookieCategory {
      // The necessary Cookies category is always present
      const necessaryCategory: CookieCategory = {
         isNecessary: true,
         category: $t("privacy-and-terms.cookie_customise.categories.necessary.title"),
         description: $t("privacy-and-terms.cookie_customise.categories.necessary.description"),
         cookies: [
            {
               id: "authentication",
               name: $t("privacy-and-terms.cookie_customise.categories.necessary.cookies.authentication.title"),
               description: $t(
                  "privacy-and-terms.cookie_customise.categories.necessary.cookies.authentication.description"
               ),
            },
            {
               id: "cookiePreferences",
               name: $t("privacy-and-terms.cookie_customise.categories.necessary.cookies.cookie_preferences.title"),
               description: $t(
                  "privacy-and-terms.cookie_customise.categories.necessary.cookies.cookie_preferences.description"
               ),
            },
         ],
      };
      if (clientSettings.intercom.isActive) {
         necessaryCategory.cookies.push({
            id: "intercom",
            name: $t("privacy-and-terms.cookie_customise.categories.necessary.cookies.intercom.title"),
            description: $t("privacy-and-terms.cookie_customise.categories.necessary.cookies.intercom.description"),
         });
      }

      return necessaryCategory;
   }

   function getAnalyticsCategory($t: (key: string) => string, clientSettings: ClientSettings): CookieCategory {
      const analyticsCategory: CookieCategory = {
         isNecessary: false,
         category: $t("privacy-and-terms.cookie_customise.categories.analytics.title"),
         description: $t("privacy-and-terms.cookie_customise.categories.analytics.description"),
         cookies: [],
      };

      if (clientSettings.metaPixelAnalytics.isActive) {
         analyticsCategory.cookies.push({
            id: "metaPixel",
            name: $t("privacy-and-terms.cookie_customise.categories.analytics.cookies.meta_pixel.title"),
            description: $t("privacy-and-terms.cookie_customise.categories.analytics.cookies.meta_pixel.description"),
            cookieStorageKey: "usr-cp-an-mp",
            isActivated: false,
            code: "457608848310959", // hardcoded meta pixel code used for all portals
         });
      }

      if (clientSettings.googleAnalytics.isActive) {
         analyticsCategory.cookies.push({
            id: "googleAnalytics",
            name: $t("privacy-and-terms.cookie_customise.categories.analytics.cookies.google_analytics.title"),
            description: $t(
               "privacy-and-terms.cookie_customise.categories.analytics.cookies.google_analytics.description"
            ),
            cookieStorageKey: "usr-cp-an-ga",
            isActivated: false,
            code: clientSettings.googleAnalytics.code,
         });
      }

      // Restore the user's previous Analytics cookie settings (Browser cookies)
      (analyticsCategory.cookies as ServiceCookie[]).forEach((setting) => {
         setting.isActivated = getCookieByName(setting.cookieStorageKey) === "1";

         // turn on meta pixel service if the user has previously accepted it
         if (setting.id === "metaPixel" && setting.isActivated) {
            useFacebookPixel(setting.code);
         }
         // turn on google service if the user has previously accepted it
         if (setting.id === "googleAnalytics" && setting.isActivated) {
            useGoogleAnalytics(setting.code);
         }
      });

      return analyticsCategory;
   }

   function populateCookieCategories(clientSettings: ClientSettings, $t: (key: string) => string) {
      cookieCategories.value = []; // clear the array while keeping ref reactivity

      const necessaryCookies = getNecessaryCategory($t, clientSettings);
      cookieCategories.value.push(necessaryCookies);

      // -----------------------------------------
      // Only add the Analytics category if at least one of the analytics cookies is active
      const analyticsCookies = getAnalyticsCategory($t, clientSettings);
      if (analyticsCookies.cookies.length > 0) {
         cookieCategories.value.push(analyticsCookies);
      }
   }

   // accept all cookies (dont change cookies that are compulsory)
   function acceptAllCookies() {
      cookieCategories.value.forEach((category) => {
         if (category.isNecessary) return;

         (category.cookies as ServiceCookie[]).forEach((setting) => {
            setting.isActivated = true;

            // set the cookie name
            setCookie(setting.cookieStorageKey, "1", cookieConsentExpiryDays); // Sets a cookie that expires in X days
         });
      });

      setConsentedOnCookie();
   }

   // reject all cookies (dont change cookies that are compulsory)
   function rejectAllCookies() {
      cookieCategories.value.forEach((category) => {
         if (category.isNecessary) return;

         (category.cookies as ServiceCookie[]).forEach((setting) => {
            setting.isActivated = false;
            // set the cookie name
            setCookie(setting.cookieStorageKey, "0", cookieConsentExpiryDays); // Sets a cookie to be disabled that expires in X days
         });
      });

      setConsentedOnCookie();
   }

   /** Toggle the cookie settings based on the user's new preferences selected in 'Customise Cookies'
    */
   function toggleCookieSettings(updatedSettings: { id: CookieId; isActivated: boolean }[]) {
      // Update the cookie settings store with the new choices
      cookieCategories.value.forEach((category) => {
         if (category.isNecessary) return;

         (category.cookies as ServiceCookie[]).forEach((setting) => {
            // find the setting with the given ID and update it
            const settingFound = updatedSettings.find((updatedSetting) => updatedSetting.id === setting.id);
            if (!settingFound) return;

            setting.isActivated = settingFound.isActivated;
            setCookie(setting.cookieStorageKey, setting.isActivated ? "1" : "0", cookieConsentExpiryDays); // Sets a cookie to be disabled that expires in X days
         });
      });

      setConsentedOnCookie();
   }

   /** Set the consentedOn cookie to the current date and reloads the page so that the changes are applied
    */
   function setConsentedOnCookie() {
      setCookie(consentedOnCookieKey, new Date().toISOString(), cookieConsentExpiryDays);

      // reload the page to apply the changes
      window.location.reload();
   }

   return {
      cookieCategories,
      populateCookieCategories,
      acceptAllCookies,
      rejectAllCookies,
      toggleCookieSettings,
      consentedOnCookieKey,
   };
});

export { useCookieSettingsStore, type CookieId, type CookieCategory, type Cookie, type ServiceCookie };
